import React from 'react';
import { Navigate } from 'react-router-dom';
import LogoLoader from './LogoLoader';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Higher-order component to protect routes based on authentication and permissions
 */
export const ProtectedRoute = ({ 
  children, 
  requireAuth = true, 
  requiredPermissions = [], 
  requiredRole = null,
  fallback = null 
}) => {
  const { isAuthenticated, loading } = useAuth();
  const { hasPermission, hasRole, hasAnyPermission } = usePermissions();

  // Show loading while checking authentication
  if (loading) {
    return <LogoLoader fullScreen />;
  }

  // Check authentication requirement
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Check role requirement
  if (requiredRole && !hasRole(requiredRole)) {
    return fallback || <Navigate to="/unauthorized" replace />;
  }

  // Check permissions requirement
  if (requiredPermissions.length > 0) {
    // Check if user has any of the required permissions
    if (Array.isArray(requiredPermissions[0])) {
      // Multiple permission groups - user needs at least one complete group
      const hasRequiredPermissions = requiredPermissions.some(permissionGroup => 
        permissionGroup.every(permission => hasPermission(permission))
      );
      
      if (!hasRequiredPermissions) {
        return fallback || <Navigate to="/unauthorized" replace />;
      }
    } else {
      // Single permission group - user needs at least one permission
      if (!hasAnyPermission(requiredPermissions)) {
        return fallback || <Navigate to="/unauthorized" replace />;
      }
    }
  }

  return children;
};

/**
 * Component that conditionally renders children based on permissions
 */
export const PermissionGate = ({ 
  children, 
  requiredPermissions = [], 
  requiredRole = null,
  fallback = null,
  requireAll = false 
}) => {
  const { hasRole, hasAnyPermission, hasAllPermissions } = usePermissions();

  // Check role requirement
  if (requiredRole && !hasRole(requiredRole)) {
    return fallback;
  }

  // Check permissions requirement
  if (requiredPermissions.length > 0) {
    const hasRequiredPermissions = requireAll 
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);
    
    if (!hasRequiredPermissions) {
      return fallback;
    }
  }

  return children;
};

/**
 * Hook to check if user can access a route
 */
export const useCanAccess = (requiredPermissions = [], requiredRole = null) => {
  const { isAuthenticated } = useAuth();
  const { hasRole, hasAnyPermission } = usePermissions();

  if (!isAuthenticated) {
    return false;
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return false;
  }

  if (requiredPermissions.length > 0 && !hasAnyPermission(requiredPermissions)) {
    return false;
  }

  return true;
};

export default ProtectedRoute;
